import { CommonModule } from '@angular/common';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Component, DestroyRef, computed, effect, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { RouterLink } from '@angular/router';
import { AllUserRoles } from '@app/enum/enumLookup';
import { RoleService } from '@app/shared/services/roles.service';
import { UiLibraryModule, EzPanelComponent } from '@template-ui';
import { catchError, forkJoin, of } from 'rxjs';

type HomeEntryPoint = {
  label: string;
  route: string;
  description: string;
};

type PlatformCapability = {
  title: string;
  audience: string;
  description: string;
  outcomes: string[];
  entry?: HomeEntryPoint;
};

type ScopeSummaryItem = {
  label: string;
  detail: string;
};

type PermissionAction = {
  code: string;
  label: string;
  description: string;
};

type DashboardStatusTag = {
  label: string;
  tone: 'live' | 'accent' | 'neutral';
};

type DashboardSummaryCard = {
  label: string;
  value: string;
  detail: string;
  kind: 'metric' | 'text';
};

type DashboardQueueCard = {
  title: string;
  audience: string;
  description: string;
  items: string[];
  source: string;
  sourceTone: 'live' | 'neutral';
};

type DashboardAlertItem = {
  title: string;
  detail: string;
  severity: 'info' | 'attention' | 'success';
  source: string;
  sourceTone: 'live' | 'neutral';
};

type DashboardActivityItem = {
  title: string;
  detail: string;
  meta: string;
  source: string;
  sourceTone: 'live' | 'neutral';
};

type LiveDataState = 'idle' | 'loading' | 'loaded' | 'unavailable';

type WorkflowWorklistPage = {
  totalCount: number;
  items: WorkflowWorklistItem[];
};

type WorkflowWorklistItem = {
  workflowCode: string;
  subjectTypeCode: string;
  subjectId: number;
  workflowStatusCode: string;
  currentStepCode?: string | null;
  currentQueueDisplayText?: string | null;
  currentAssigneeDisplayName?: string | null;
  statusChangedUtc: string;
};

type ReadyToScheduleProjection = {
  proceedingNumber: string;
  subjectTypeCode: string;
  subjectKey: string;
  title: string;
  schedulingStatusCode: string;
  earliestScheduleStartUtc?: string | null;
  locationName?: string | null;
  roomName?: string | null;
};

type ProceedingCalendarProjection = {
  proceedingNumber: string;
  title: string;
  schedulingStatusCode: string;
  scheduledStartUtc?: string | null;
  scheduledEndUtc?: string | null;
  locationName?: string | null;
  roomName?: string | null;
};

type PlatformHomeLiveData = {
  worklist: WorkflowWorklistPage;
  readyToSchedule: ReadyToScheduleProjection[];
  calendar: ProceedingCalendarProjection[];
};

const PLATFORM_ADMIN_ROLE = 'platform_admin';
const WORKFLOW_RUNTIME_READ_PERMISSION = 'workflow_runtime.read';
const LIVE_SOURCE = 'Live access profile';
const LIVE_WORKFLOW_SOURCE = 'Live workflow runtime';
const LIVE_SCHEDULING_SOURCE = 'Live scheduling projections';
const QUIET_HTTP_OPTIONS = {
  headers: new HttpHeaders({
    'skip-error-interceptor': 'yes',
    'skip-loading-interceptor': 'yes',
  }),
  withCredentials: true,
};

const ORDER_SUMMARY_ACTIONS: readonly PermissionAction[] = [
  {
    code: 'order_summary.submit',
    label: 'Submit order summaries',
    description: 'Create or submit the current JMS pilot order-summary work.',
  },
  {
    code: 'order_summary.cancel',
    label: 'Cancel submitted order summaries',
    description: 'Reverse or stop work that should no longer proceed.',
  },
  {
    code: 'order_summary.request_expedite',
    label: 'Request expedited handling',
    description: 'Escalate time-sensitive pilot work without a separate entitlement check.',
  },
  {
    code: 'order_summary.apply_discount',
    label: 'Apply approved discounts',
    description: 'Adjust the current pilot workflow when approved pricing changes apply.',
  },
  {
    code: 'order_summary.plan_fulfillment',
    label: 'Plan fulfillment coordination',
    description: 'Coordinate downstream fulfillment planning inside the pilot scope.',
  },
];

function normalizeCode(value: string | null | undefined): string {
  return (value ?? '').trim().toLowerCase();
}

function formatCount(count: number, singular: string, plural = `${singular}s`): string {
  return `${count} ${count === 1 ? singular : plural}`;
}

function formatMetricValue(count: number): string {
  return count.toString().padStart(2, '0');
}

function formatCode(value: string | null | undefined): string {
  const normalized = normalizeCode(value);
  if (!normalized) {
    return 'Unspecified';
  }

  return normalized
    .split(/[_\-.]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

@Component({
    selector: 'phms-platform-home',
    imports: [CommonModule, RouterLink, UiLibraryModule, EzPanelComponent],
    templateUrl: './platform-home.component.html',
    styleUrls: ['./platform-home.component.scss']
})
export class PlatformHomeComponent {
  private readonly http = inject(HttpClient);
  private readonly roleService = inject(RoleService);
  private readonly destroyRef = inject(DestroyRef);
  private loadedLiveDataKey: string | null = null;

  private readonly liveDataState = signal<LiveDataState>('idle');
  private readonly liveDataError = signal<string | null>(null);
  private readonly liveData = signal<PlatformHomeLiveData>({
    worklist: { totalCount: 0, items: [] },
    readyToSchedule: [],
    calendar: [],
  });

  readonly dashboardGuardrails = [
    'Only routes already supported by the protected shell appear as quick actions here.',
    'Unsupported modules stay hidden until the current-user access profile resolves them explicitly.',
    'Operational cards use current-user access and fail closed when workflow runtime data is unavailable.',
  ];

  readonly pilotProofPoints = [
    'JMS remains a pilot application that validates the platform foundation rather than defining the platform brand.',
    'Pilot workflows now consume the same identity, workflow worklist, and scheduling projection path that future client modules can reuse.',
    'The protected home can show real C3 scheduling-readiness information without changing post-login routing.',
  ];

  readonly extensionPaths = [
    'Extend the live scheduling cards with detail navigation when proceeding-detail screens are ready.',
    'Keep protected navigation, unauthorized handling, and post-login routing stable while the dashboard surface evolves.',
    'Extend this home with client- or domain-specific modules without reintroducing page-specific entitlement APIs.',
  ];

  private readonly normalizedRoleCodes = computed(() =>
    new Set(this.roleService.roles().map((code) => normalizeCode(code))),
  );

  private readonly normalizedPermissionCodes = computed(() =>
    new Set(this.roleService.permissionCodes().map((code) => normalizeCode(code))),
  );

  private readonly canReadWorkflowRuntime = computed(() =>
    this.normalizedPermissionCodes().has(WORKFLOW_RUNTIME_READ_PERMISSION),
  );

  readonly liveDataStatusLabel = computed(() => {
    if (!this.canReadWorkflowRuntime()) {
      return 'Workflow runtime not granted';
    }

    switch (this.liveDataState()) {
      case 'loading':
        return 'Loading live operational data';
      case 'loaded':
        return 'Live operational data loaded';
      case 'unavailable':
        return 'Live operational data unavailable';
      default:
        return 'Live operational data pending';
    }
  });

  constructor() {
    effect(() => {
      const profileState = this.roleService.accessProfileState();
      const canReadWorkflowRuntime = this.canReadWorkflowRuntime();
      const actorKey = this.actorIdLabel();

      if (profileState !== 'loaded') {
        return;
      }

      if (!canReadWorkflowRuntime) {
        this.loadedLiveDataKey = null;
        this.liveDataState.set('idle');
        this.liveDataError.set(null);
        return;
      }

      if (this.loadedLiveDataKey === actorKey && this.liveDataState() !== 'unavailable') {
        return;
      }

      this.loadedLiveDataKey = actorKey;
      this.loadLiveOperationalData();
    });
  }

  readonly actorDisplayName = computed(() => {
    const actor = this.roleService.accessProfile()?.actor;
    return actor?.displayName?.trim() || actor?.email?.trim() || actor?.username?.trim() || 'Authenticated user';
  });

  readonly actorProviderLabel = computed(() => {
    const providerCode = this.roleService.accessProfile()?.actor.providerCode?.trim();
    return providerCode?.length ? providerCode : 'Resolved via current-user access profile';
  });

  readonly actorIdLabel = computed(() => {
    const actorId = this.roleService.accessProfile()?.actor.actorId?.trim();
    return actorId?.length ? actorId : 'Unavailable';
  });

  readonly actorUserIdLabel = computed(() => {
    const userId = this.roleService.accessProfile()?.actor.userId;
    return typeof userId === 'number' ? userId.toString() : 'Unavailable';
  });

  readonly roleBadges = computed(() => {
    const roles = this.roleService.accessProfile()?.roles ?? [];

    return roles.map((role) => {
      const roleName = role.name?.trim();
      const roleCode = role.code?.trim() ?? '';

      if (!roleName || normalizeCode(roleName) === normalizeCode(roleCode)) {
        return roleCode;
      }

      return `${roleName} (${roleCode})`;
    });
  });

  readonly scopeSummaries = computed<ScopeSummaryItem[]>(() => {
    const counts = new Map<string, number>();

    for (const scope of this.roleService.scopes()) {
      const scopeTypeCode = normalizeCode(scope.scopeTypeCode) || 'unspecified';
      counts.set(scopeTypeCode, (counts.get(scopeTypeCode) ?? 0) + 1);
    }

    return Array.from(counts.entries()).map(([scopeTypeCode, count]) => ({
      label: this.describeScopeType(scopeTypeCode),
      detail: count === 1 ? '1 assignment' : `${count} assignments`,
    }));
  });

  readonly grantedPermissionActions = computed(() =>
    ORDER_SUMMARY_ACTIONS.filter((action) => this.normalizedPermissionCodes().has(action.code)),
  );

  readonly dashboardStance = computed(() => {
    if (this.hasRole(PLATFORM_ADMIN_ROLE) && this.hasRole(AllUserRoles.DEVTEAM)) {
      return 'Admin + developer session';
    }

    if (this.hasRole(PLATFORM_ADMIN_ROLE)) {
      return 'Platform admin session';
    }

    if (this.grantedPermissionActions().length > 0) {
      return 'Workflow-enabled session';
    }

    return 'Protected shell session';
  });

  readonly dashboardStatusTags = computed<DashboardStatusTag[]>(() => {
    const tags: DashboardStatusTag[] = [
      { label: 'Protected route', tone: 'live' },
      { label: 'Live access profile', tone: 'live' },
      { label: this.dashboardStance(), tone: 'accent' },
      { label: this.liveDataStatusLabel(), tone: this.liveDataState() === 'loaded' ? 'live' : 'neutral' },
    ];

    if (this.hasRole(AllUserRoles.DEVTEAM)) {
      tags.push({ label: 'Developer tools visible', tone: 'neutral' });
    } else {
      tags.push({ label: 'JMS pilot bounded', tone: 'neutral' });
    }

    return tags;
  });

  readonly dashboardIntro = computed(() => {
    if (this.hasRole(PLATFORM_ADMIN_ROLE) && this.grantedPermissionActions().length > 0) {
      return 'Shared governance posture, current JMS pilot workflow access, and implemented workflow/scheduling projections now resolve from the protected landing page.';
    }

    if (this.hasRole(PLATFORM_ADMIN_ROLE)) {
      return 'Governance posture is live for this session while unsupported workflow modules remain bounded to explicit pilot permissions.';
    }

    if (this.grantedPermissionActions().length > 0) {
      return 'Workflow actions are available for this session, while unsupported governance modules remain hidden until the access profile changes.';
    }

    return 'This protected dashboard stays active even when the current session resolves only a narrower platform access posture.';
  });

  readonly visibleCapabilities = computed<PlatformCapability[]>(() => {
    const capabilities: PlatformCapability[] = [];
    const permissionActions = this.grantedPermissionActions();

    if (this.hasRole(PLATFORM_ADMIN_ROLE)) {
      capabilities.push({
        title: 'Admin governance controls',
        audience: 'Visible only when the resolved role set includes platform_admin.',
        description:
          'The seeded platform_admin role is active for this session, so the dashboard can surface governance-oriented spotlight content without broadening the auth contract.',
        outcomes: [
          'Access reviews, shared policy posture, and protected-home guardrails can stay inside the existing IAM baseline.',
          permissionActions.length > 0
            ? `${formatCount(permissionActions.length, 'current JMS pilot permission')} resolve alongside the administrator role.`
            : 'No additional JMS pilot permission list is implied beyond the resolved administrator posture.',
        ],
      });
    }

    if (permissionActions.length > 0) {
      capabilities.push({
        title: 'Pilot order-summary actions',
        audience: 'Visible only when the access profile grants one or more current JMS pilot permissions.',
        description:
          'This spotlight stays tied to the current-user access profile and lists only the JMS pilot actions that the resolved session can actually perform.',
        outcomes: permissionActions.map((action) => `${action.label}: ${action.description}`),
        entry: {
          label: 'Open dashboard',
          route: '/dashboard',
          description: 'Continue into the protected shell; this redesign intentionally avoids inventing new workflow routes.',
        },
      });
    }

    if (this.canReadWorkflowRuntime()) {
      capabilities.push({
        title: 'Workflow and scheduling runtime',
        audience: 'Visible only when workflow_runtime.read resolves from the current access profile.',
        description:
          'Gate B/C3 runtime surfaces are connected: workflow worklists, ready-to-schedule projections, and the scheduled-proceeding calendar are read from live endpoints.',
        outcomes: [
          `${formatCount(this.liveData().worklist.totalCount, 'workflow item')} currently match the platform worklist query.`,
          `${formatCount(this.liveData().readyToSchedule.length, 'ready-to-schedule proceeding')} returned from the scheduling readiness projection.`,
          `${formatCount(this.liveData().calendar.length, 'scheduled proceeding')} returned for the next 30 days.`,
        ],
      });
    }

    if (this.hasRole(AllUserRoles.DEVTEAM)) {
      capabilities.push({
        title: 'Developer shared tools',
        audience: 'Visible only when the resolved role set includes DEVTEAM.',
        description:
          'Developer-only surfaces stay behind the same current-user access profile so standard users do not see implementation tooling from the dashboard.',
        outcomes: [
          'Protected routing to the EZ Library showcase stays explicit and reviewable.',
          'Theme and component validation remains bounded to developer sessions only.',
        ],
        entry: {
          label: 'Open Library Showcase',
          route: '/libraryShowcase',
          description: 'Developer-only shared component catalog inside the protected shell.',
        },
      });
    }

    return capabilities;
  });

  readonly visibleEntryPoints = computed<HomeEntryPoint[]>(() => {
    const entryPoints: HomeEntryPoint[] = [
      {
        label: 'Open dashboard',
        route: '/dashboard',
        description: 'Continue into the protected workspace while the pilot route set stays intentionally small.',
      },
    ];

    if (this.normalizedPermissionCodes().has('inmate_offense_record.read')) {
      entryPoints.push({
        label: 'Open inmate case context',
        route: '/case-context/inmate/9304',
        description: 'Open Rita Readyset\'s seeded case context, including three completed workflow steps, Hearing in progress, and its approved entry attestation.',
      });
    }

    if (this.hasRole(AllUserRoles.DEVTEAM)) {
      entryPoints.push({
        label: 'Open Library Showcase',
        route: '/libraryShowcase',
        description: 'Developer-only shared UI catalog behind the existing protected route contract.',
      });
    }

    return entryPoints;
  });

  readonly dashboardSummaryCards = computed<DashboardSummaryCard[]>(() => {
    const roleLabels = this.roleBadges();
    const scopeLabels = this.scopeSummaries();
    const liveData = this.liveData();

    return [
      {
        label: 'Resolved roles',
        value: formatMetricValue(roleLabels.length),
        detail: roleLabels.length > 0 ? roleLabels.slice(0, 2).join(' / ') : 'No roles returned for this session.',
        kind: 'metric',
      },
      {
        label: 'Workflow worklist',
        value: this.canReadWorkflowRuntime() ? formatMetricValue(liveData.worklist.totalCount) : '--',
        detail: this.canReadWorkflowRuntime()
          ? `${formatCount(liveData.worklist.items.length, 'row')} loaded from the platform worklist endpoint.`
          : 'Current session does not grant workflow runtime read access.',
        kind: 'metric',
      },
      {
        label: 'Ready to schedule',
        value: this.canReadWorkflowRuntime() ? formatMetricValue(liveData.readyToSchedule.length) : '--',
        detail: this.canReadWorkflowRuntime()
          ? 'Rows loaded from the C3 ready-to-schedule projection.'
          : 'Scheduling projections require workflow runtime read access.',
        kind: 'metric',
      },
      {
        label: 'Next 30 days',
        value: this.canReadWorkflowRuntime() ? formatMetricValue(liveData.calendar.length) : '--',
        detail: scopeLabels.length > 0 ? scopeLabels.map((scope) => scope.label).join(', ') : 'No scope assignments returned.',
        kind: 'metric',
      },
    ];
  });

  readonly dashboardQueues = computed<DashboardQueueCard[]>(() => {
    if (!this.canReadWorkflowRuntime()) {
      return [
        {
          title: 'Protected-home access posture',
          audience: 'Visible to every authenticated user.',
          description:
            'This session can render the protected home, but live workflow and scheduling cards are withheld because workflow runtime read access is not resolved.',
          items: [
            `${formatCount(this.roleBadges().length, 'role')} returned by the current-user access profile.`,
            `${formatCount(this.normalizedPermissionCodes().size, 'permission')} returned by the current-user access profile.`,
            `${formatCount(this.roleService.scopes().length, 'scope assignment')} returned by the current-user access profile.`,
          ],
          source: LIVE_SOURCE,
          sourceTone: 'live',
        },
      ];
    }

    const liveData = this.liveData();

    return [
      {
        title: 'Workflow runtime worklist',
        audience: 'Visible when workflow_runtime.read is granted.',
        description:
          'Authoritative workflow rows from the platform worklist reader, including queue labels and current assignment posture.',
        items: this.formatWorklistItems(liveData.worklist.items),
        source: LIVE_WORKFLOW_SOURCE,
        sourceTone: 'live',
      },
      {
        title: 'Ready-to-schedule proceedings',
        audience: 'Visible when workflow_runtime.read is granted.',
        description:
          'C3 scheduling readiness projection rows for proceedings that are ready or continued and need scheduling attention.',
        items: this.formatReadyToScheduleItems(liveData.readyToSchedule),
        source: LIVE_SCHEDULING_SOURCE,
        sourceTone: 'live',
      },
      {
        title: 'Scheduled proceedings',
        audience: 'Visible when workflow_runtime.read is granted.',
        description:
          'Scheduled-proceeding calendar projection rows for the next 30 days, ordered by scheduled start.',
        items: this.formatCalendarItems(liveData.calendar),
        source: LIVE_SCHEDULING_SOURCE,
        sourceTone: 'live',
      },
    ];
  });

  readonly dashboardAlerts = computed<DashboardAlertItem[]>(() => {
    const alerts: DashboardAlertItem[] = [
      {
        title: this.canReadWorkflowRuntime() ? 'Workflow runtime data is connected' : 'Workflow runtime data is withheld',
        detail:
          this.canReadWorkflowRuntime()
            ? 'The home page reads the implemented worklist and scheduling projection endpoints when the current profile grants workflow_runtime.read.'
            : 'The home page keeps workflow and scheduling operational cards closed because this session does not grant workflow_runtime.read.',
        severity: this.canReadWorkflowRuntime() ? 'success' : 'attention',
        source: this.canReadWorkflowRuntime() ? LIVE_WORKFLOW_SOURCE : LIVE_SOURCE,
        sourceTone: 'live',
      },
      {
        title: 'Fail-closed visibility stays active',
        detail:
          'Unsupported quick actions and capability spotlights remain hidden until the current-user access profile resolves them explicitly.',
        severity: 'success',
        source: LIVE_SOURCE,
        sourceTone: 'live',
      },
    ];

    if (this.liveDataState() === 'unavailable' && this.liveDataError()) {
      alerts.push({
        title: 'Live operational data could not be loaded',
        detail: this.liveDataError() ?? 'The live workflow and scheduling endpoints did not return data for this session.',
        severity: 'attention',
        source: LIVE_WORKFLOW_SOURCE,
        sourceTone: 'neutral',
      });
    }

    if (!this.hasRole(PLATFORM_ADMIN_ROLE)) {
      alerts.push({
        title: 'Admin-only governance modules are suppressed',
        detail:
          'This session does not resolve platform_admin, so administrator-only queue and spotlight content remains hidden from the dashboard.',
        severity: 'attention',
        source: LIVE_SOURCE,
        sourceTone: 'live',
      });
    }

    if (this.grantedPermissionActions().length === 0) {
      alerts.push({
        title: 'No order-summary actions are currently mapped',
        detail:
          'The dashboard omits the order-summary spotlight when the current-user access profile does not grant JMS pilot order-summary actions.',
        severity: 'attention',
        source: LIVE_SOURCE,
        sourceTone: 'live',
      });
    }

    if (this.hasRole(AllUserRoles.DEVTEAM)) {
      alerts.push({
        title: 'Developer tooling is available in this session',
        detail: 'Developer-only quick actions remain visible here because DEVTEAM resolves from the same current-user access profile.',
        severity: 'info',
        source: LIVE_SOURCE,
        sourceTone: 'live',
      });
    }

    return alerts;
  });

  readonly dashboardActivityFeed = computed<DashboardActivityItem[]>(() => {
    const activities: DashboardActivityItem[] = [
      {
        title: 'Session resolved',
        detail: `Signed in as ${this.actorDisplayName()} via ${this.actorProviderLabel()}.`,
        meta: `Actor ${this.actorIdLabel()} | User ${this.actorUserIdLabel()}`,
        source: LIVE_SOURCE,
        sourceTone: 'live',
      },
      {
        title: 'Quick actions bounded to supported routes',
        detail: `${formatCount(this.visibleEntryPoints().length, 'protected route')} are currently advertised from this dashboard.`,
        meta: 'Visibility remains fail-closed from the current-user access profile.',
        source: LIVE_SOURCE,
        sourceTone: 'live',
      },
    ];

    if (this.grantedPermissionActions().length > 0) {
      activities.push({
        title: 'Pilot order-summary access resolved',
        detail: `${formatCount(this.grantedPermissionActions().length, 'JMS pilot action')} currently shape the visible order-summary spotlight content.`,
        meta: this.grantedPermissionActions()
          .slice(0, 2)
          .map((action) => action.label)
          .join(' | '),
        source: LIVE_SOURCE,
        sourceTone: 'live',
      });
    }

    if (this.canReadWorkflowRuntime()) {
      activities.push({
        title: 'Workflow and scheduling projections queried',
        detail: `${formatCount(this.liveData().worklist.items.length, 'worklist row')}, ${formatCount(this.liveData().readyToSchedule.length, 'ready row')}, and ${formatCount(this.liveData().calendar.length, 'calendar row')} are currently loaded for this session.`,
        meta: this.liveDataStatusLabel(),
        source: LIVE_WORKFLOW_SOURCE,
        sourceTone: this.liveDataState() === 'loaded' ? 'live' : 'neutral',
      });
    }

    return activities;
  });

  private loadLiveOperationalData(): void {
    this.liveDataState.set('loading');
    this.liveDataError.set(null);

    const now = new Date();
    const thirtyDaysFromNow = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000);

    forkJoin({
      worklist: this.http.get<WorkflowWorklistPage>(
        '/api/platform/workflows/worklist',
        {
          ...QUIET_HTTP_OPTIONS,
          params: new HttpParams()
            .set('pageNumber', '1')
            .set('pageSize', '5'),
        },
      ),
      readyToSchedule: this.http.get<ReadyToScheduleProjection[]>(
        '/api/v1/scheduling/projections/ready-to-schedule',
        {
          ...QUIET_HTTP_OPTIONS,
          params: new HttpParams().set('take', '5'),
        },
      ),
      calendar: this.http.get<ProceedingCalendarProjection[]>(
        '/api/v1/scheduling/projections/calendar',
        {
          ...QUIET_HTTP_OPTIONS,
          params: new HttpParams()
            .set('startUtc', now.toISOString())
            .set('endUtc', thirtyDaysFromNow.toISOString())
            .set('take', '5'),
        },
      ),
    }).pipe(
      catchError((error: unknown) => {
        this.liveDataState.set('unavailable');
        this.liveDataError.set(this.describeLiveDataError(error));
        return of(null);
      }),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe((result) => {
      if (!result) {
        return;
      }

      this.liveData.set({
        worklist: result.worklist ?? { totalCount: 0, items: [] },
        readyToSchedule: result.readyToSchedule ?? [],
        calendar: result.calendar ?? [],
      });
      this.liveDataState.set('loaded');
    });
  }

  private formatWorklistItems(items: WorkflowWorklistItem[]): string[] {
    if (this.liveDataState() === 'loading') {
      return ['Loading platform worklist rows...'];
    }

    if (items.length === 0) {
      return ['No workflow worklist rows returned for the current query.'];
    }

    return items.map((item) => {
      const queue = item.currentQueueDisplayText?.trim() || formatCode(item.currentStepCode);
      const subject = `${formatCode(item.subjectTypeCode)} ${item.subjectId}`;
      const assignee = item.currentAssigneeDisplayName?.trim() || 'Unassigned';

      return `${queue}: ${subject} is ${formatCode(item.workflowStatusCode)} (${assignee}).`;
    });
  }

  private formatReadyToScheduleItems(items: ReadyToScheduleProjection[]): string[] {
    if (this.liveDataState() === 'loading') {
      return ['Loading ready-to-schedule projection rows...'];
    }

    if (items.length === 0) {
      return ['No ready-to-schedule proceedings returned for the current query.'];
    }

    return items.map((item) => {
      const windowStart = this.formatDateTime(item.earliestScheduleStartUtc);
      const location = this.formatLocation(item.locationName, item.roomName);

      return `${item.proceedingNumber}: ${item.title} (${formatCode(item.schedulingStatusCode)})${windowStart ? `, earliest ${windowStart}` : ''}${location ? `, ${location}` : ''}.`;
    });
  }

  private formatCalendarItems(items: ProceedingCalendarProjection[]): string[] {
    if (this.liveDataState() === 'loading') {
      return ['Loading scheduled-proceeding calendar rows...'];
    }

    if (items.length === 0) {
      return ['No scheduled proceedings returned for the next 30 days.'];
    }

    return items.map((item) => {
      const scheduledStart = this.formatDateTime(item.scheduledStartUtc) ?? 'No start time';
      const location = this.formatLocation(item.locationName, item.roomName);

      return `${item.proceedingNumber}: ${item.title} at ${scheduledStart}${location ? `, ${location}` : ''}.`;
    });
  }

  private formatDateTime(value: string | null | undefined): string | null {
    if (!value) {
      return null;
    }

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return null;
    }

    return new Intl.DateTimeFormat(undefined, {
      dateStyle: 'medium',
      timeStyle: 'short',
    }).format(date);
  }

  private formatLocation(locationName: string | null | undefined, roomName: string | null | undefined): string | null {
    const parts = [locationName, roomName]
      .map((part) => part?.trim())
      .filter((part): part is string => Boolean(part));

    return parts.length > 0 ? parts.join(' / ') : null;
  }

  private describeLiveDataError(error: unknown): string {
    if (typeof error === 'object' && error !== null && 'status' in error) {
      const status = (error as { status?: unknown }).status;
      if (status === 401) {
        return 'The live workflow and scheduling endpoints require an authenticated session.';
      }

      if (status === 403) {
        return 'The current access profile was denied by the live workflow or scheduling projection endpoint.';
      }
    }

    return 'The live workflow or scheduling projection endpoint did not return data for this session.';
  }

  private hasRole(roleCode: string): boolean {
    return this.normalizedRoleCodes().has(normalizeCode(roleCode));
  }

  private describeScopeType(scopeTypeCode: string): string {
    switch (scopeTypeCode) {
      case 'global':
        return 'Global coverage';
      case 'county':
        return 'County-scoped coverage';
      case 'facility':
        return 'Facility-scoped coverage';
      default:
        return `${scopeTypeCode} scope coverage`;
    }
  }
}
